import { MagnifyingGlassIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { deleteBrokerConditionCategory, getBrokerConditionCategories } from 'services/apis/conditions'
import { svgLoading } from 'stories/assets'
import { Button, Input2 } from 'stories/components'
import { confirm } from 'utils'

import { BrokerConditionCateogryDetails } from './details'
import type { IBrokerConditionCategory } from './types'

export function BrokerConditionCategories() {
  const [query, setQuery] = useState('')
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [categories, setCategories] = useState<Array<IBrokerConditionCategory>>([])
  const [isEditing, setIsEditing] = useState(false)
  const [lastUpdatedAt, setLastUpdatedAt] = useState(Date.now())
  const [selectedIndex, setSelectedIndex] = useState(-1)

  useEffect(() => {
    refresh()
  }, [])

  const refresh = () => {
    setIsLoading(true)
    getBrokerConditionCategories()
      .then(({ data }) => {
        setTotal(data.length)
        setCategories(data)
      })
      .finally(() => setIsLoading(false))
  }

  if (isEditing)
    return (
      <BrokerConditionCateogryDetails
        lastUpdatedAt={lastUpdatedAt}
        defaults={selectedIndex == -1 ? null : categories[selectedIndex]}
        onBack={() => setIsEditing(false)}
        onComplete={refresh}
      />
    )

  const onAdd = () => {
    setLastUpdatedAt(Date.now())
    setSelectedIndex(-1)
    setIsEditing(true)
  }

  const onEdit = (index: number) => {
    setLastUpdatedAt(Date.now())
    setSelectedIndex(index)
    setIsEditing(true)
  }

  const onTrash = async (index: number) => {
    const { title } = categories[index]
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Do you want to remove this category?
        <br />
        <span className="text-gray-600">Category title: {title}</span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    setIsLoading(true)
    await deleteBrokerConditionCategory(title)
    setIsLoading(false)
    refresh()
  }

  return (
    <div className="Notess-container">
      <h2 className="text-2xl font-bold flex items-center mb-3">
        Loan Setup Condition Categories
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h2>
      <div className="flex flex-wrap justify-between mb-3">
        <div className="flex items-center flex-wrap">
          <div className="md:w-96 w-72">
            <Input2
              type="search"
              title="Search"
              hasIcon
              icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />}
              value={query}
              onChange={(value) => setQuery(value)}
            />
          </div>
          <p className="ml-5">- {total} categories</p>
        </div>
        <div className="w-32">
          <Button full onClick={onAdd}>
            Add
          </Button>
        </div>
      </div>

      <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-3 py-3">
                No
              </th>
              <th scope="col" className="py-3 px-3">
                Title
              </th>
              <th scope="col" className="px-3">
                Description
              </th>
              <th scope="col" className="px-3">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="">
            <tr className="border-b bg-white">
              <td className="px-3">1</td>
              <td className="px-3 py-3">Required</td>
              <td className="px-3 py-3"></td>
              <td className="px-3"></td>
            </tr>
            <tr className="border-b bg-slate-50">
              <td className="px-3">2</td>
              <td className="px-3 py-3">Optional</td>
              <td className="px-3"></td>
              <td className="px-3"></td>
            </tr>
            {categories.map((item: any, index) => {
              index = index + 2
              if (query && !`${item.title.toLowerCase()} ${item.notes.toLowerCase()}`.includes(query.toLowerCase()))
                return null
              return (
                <tr className={`border-b ${index % 2 ? 'bg-slate-50' : 'bg-white'}`} key={`${index}`}>
                  <td scope="row" className="px-3 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                    {index + 1}
                  </td>
                  <td className="px-3">{item.title}</td>
                  <td className="px-3">{item.notes}</td>
                  <td className="px-3">
                    <span className="flex">
                      <span
                        className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
                        onClick={() => onEdit(index - 2)}
                      >
                        <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
                      </span>
                      <span
                        className="text-red-800 p-1 hover-shadow1 cursor-pointer"
                        onClick={() => onTrash(index - 2)}
                      >
                        <TrashIcon className="w-4 h-4"></TrashIcon>
                      </span>
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
